import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Notebook, Prohibit, PencilSimpleLine } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { useFormat, useI18n } from '@/i18n'
import { istDateKey } from '@/lib/ist'
import { absDiffGrams, formatGrams } from '@/lib/weight'
import {
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  KgInput,
  PhotoCapture,
  PickerSheet,
  Sheet,
  Skeleton,
  useToast,
} from '@/ui'
import type { CommitDeliveryPayload } from '../commands'
import { ActionBar, GateScreen } from '../components/GateScreen'
import { RollConfirm, type PendingRoll } from '../components/RollConfirm'
import { useSupervisorPin } from '../components/SupervisorPin'
import { openDay, spoilSlip } from '../data/actions'
import { useGate } from '../data/useGate'
import type { GateSession } from '../data/session'
import { missingSlips, spoiledSlips } from '../model/day'
import { istInstant } from '../model/codes'
import { DeliveryPage, type PaperSlipHeader } from './delivery/DeliveryPage'
import { CloseDayForm } from './CloseDayPage'
import { BiText } from '../components/GateScreen'

/**
 * F4-B3 — कागज़ से भरना · Paper back-entry: open the PAPER session from the cover page, type in
 * each slip through the delivery screens (capture_mode PAPER_BACKFILL), mark spoiled slips, and
 * close only when the used slip range has no gap.
 */
export function PaperDayPage() {
  const g = useGate()
  const [typing, setTyping] = useState<PaperSlipHeader | null>(null)
  if (!g.ready || !g.roster) return <Skeleton className="h-64 w-full" />
  const s = g.paper
  if (!s) return <OpenPaperDay />
  const draft = g.drafts.find(
    (x) => x.data.capture_mode === 'PAPER_BACKFILL' && x.data.session_id === s.id,
  )
  if (draft || typing)
    return (
      <DeliveryPage paper paperSlip={typing ?? undefined} onPaperDone={() => setTyping(null)} />
    )
  return <PaperDayHome session={s} onType={setTyping} />
}

function OpenPaperDay() {
  const { t } = useI18n()
  const toast = useToast()
  const actor = useActor()
  const navigate = useNavigate()
  const g = useGate()
  const sup = useSupervisorPin()
  const [now] = useState(() => Date.now())
  const operators = (g.roster?.team ?? []).filter((m) =>
    hasAnyRole(m.roles, [ROLES.VILLAGE_OPERATOR]),
  )
  const [operatorId, setOperatorId] = useState<string | null>(
    hasAnyRole(actor.roles, [ROLES.VILLAGE_OPERATOR]) ? actor.user_id : null,
  )
  const [bookId, setBookId] = useState<string | null>(null)
  const [date, setDate] = useState(() => istDateKey(now))
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [read, setRead] = useState<number | null>(null)
  const [tagRoll, setTagRoll] = useState<{ roll: PendingRoll; actorId: string } | null>(null)
  const [sealRoll, setSealRoll] = useState<{ roll: PendingRoll; actorId: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const used = useMemo(() => [...g.used.bound, ...g.used.voided], [g.used])
  if (!g.roster) return null
  const r = g.roster
  const books = r.receiptBooks.filter(
    (b) => !operatorId || !b.issued_to_id || b.issued_to_id === operatorId,
  )
  const book = books.find((b) => b.id === bookId) ?? null
  const op = operators.find((o) => o.user_id === operatorId) ?? null
  const startIso = istInstant(date, start)
  const endIso = end ? istInstant(date, end) : null
  const testOff =
    read !== null && absDiffGrams(read, r.config.test_weight_g) > r.config.test_weight_tolerance_g
  const ok =
    !!op &&
    !!book &&
    !!startIso &&
    (!end || !!endIso) &&
    read !== null &&
    !!tagRoll &&
    !!sealRoll &&
    g.info.registration

  return (
    <GateScreen title="gate.paper.title">
      <Banner tone="info">{t('gate.paper.explain')}</Banner>
      <Card>
        <CardHeader eyebrow={t('gate.paper.cover')} />
        <CardBody className="flex flex-col gap-3">
          <Field label={t('gate.paper.operator')} required>
            <PickerSheet
              title={t('gate.paper.operator')}
              value={operatorId}
              onChange={(v) => {
                setOperatorId(v)
                setTagRoll(null)
                setSealRoll(null)
              }}
              options={operators.map((o) => ({ value: o.user_id, label: o.name }))}
            />
          </Field>
          <Field
            label={t('gate.paper.book')}
            required
            hint={books.length === 0 ? t('gate.paper.noBook') : undefined}
          >
            <PickerSheet
              title={t('gate.paper.book')}
              value={bookId}
              onChange={setBookId}
              options={books.map((b) => ({
                value: b.id,
                label: `${t('gate.paper.bookNo')} ${b.book_no}`,
                description: `${b.first_slip_no}–${b.last_slip_no}`,
              }))}
            />
          </Field>
          <Field label={t('gate.field.date')} required>
            <Input
              size="touch"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('gate.paper.startTime')} required>
              <Input
                size="touch"
                type="time"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </Field>
            <Field label={t('gate.paper.endTime')}>
              <Input
                size="touch"
                type="time"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </Field>
          </div>
          <Field
            label={t('gate.paper.testRead', { kg: formatGrams(r.config.test_weight_g) })}
            required
            hint={testOff ? t('gate.paper.testOff') : undefined}
          >
            <KgInput value={read} onChange={setRead} />
          </Field>
        </CardBody>
      </Card>
      {op && (
        <>
          <Card>
            <CardHeader eyebrow={t('gate.roll.tagRoll')} description={t('gate.paper.rollHint')} />
            <CardBody>
              <RollConfirm
                kind="BAG"
                operatorId={op.user_id}
                rolls={r.rolls}
                used={used}
                confirmed={tagRoll}
                onConfirm={(roll, by) => setTagRoll({ roll, actorId: by ?? actor.user_id })}
                onClear={() => setTagRoll(null)}
                requestSupervisor={(why) => sup.request(why)}
              />
            </CardBody>
          </Card>
          <Card>
            <CardHeader eyebrow={t('gate.roll.sealStrip')} />
            <CardBody>
              <RollConfirm
                kind="SEAL"
                operatorId={op.user_id}
                rolls={r.rolls}
                used={used}
                confirmed={sealRoll}
                onConfirm={(roll, by) => setSealRoll({ roll, actorId: by ?? actor.user_id })}
                onClear={() => setSealRoll(null)}
                requestSupervisor={(why) => sup.request(why)}
              />
            </CardBody>
          </Card>
        </>
      )}
      <ActionBar>
        <Button
          size="xl"
          fullWidth
          disabled={!ok}
          loading={busy}
          onClick={async () => {
            const reg = g.info.registration
            if (!ok || !op || !book || !startIso || read === null || !tagRoll || !sealRoll || !reg)
              return
            setBusy(true)
            try {
              await openDay({
                actorId: actor.user_id,
                rosterVersion: g.info.rosterVersion,
                receiptBookId: book.id,
                rolls: [tagRoll, sealRoll],
                session: {
                  capture_mode: 'PAPER',
                  group_id: reg.team.id,
                  village_name: reg.team.name,
                  operator_id: op.user_id,
                  operator_name: op.name,
                  opened_at: startIso,
                  scale_equipment_id: null,
                  scale_label: null,
                  stamp_ok: null,
                  test_weight_g: r.config.test_weight_g,
                  test_weight_read_g: read,
                  tags_in_hand: tagRoll.roll.count,
                  seals_in_hand: sealRoll.roll.count,
                  paper: {
                    book_id: book.id,
                    book_no: book.book_no,
                    day_opened_at: startIso,
                    day_closed_at: endIso,
                  },
                },
              })
              toast.success(t('gate.paper.opened'))
            } catch (e) {
              toast.error(t('gate.saveFailed'), e instanceof Error ? e.message : String(e))
            } finally {
              setBusy(false)
            }
          }}
        >
          {t('gate.paper.open')}
        </Button>
        <Button size="touch" variant="ghost" fullWidth onClick={() => navigate('/m/gate')}>
          {t('common.cancel')}
        </Button>
      </ActionBar>
      {sup.sheet}
    </GateScreen>
  )
}

function PaperDayHome({
  session,
  onType,
}: {
  session: GateSession
  onType: (h: PaperSlipHeader) => void
}) {
  const { t } = useI18n()
  const fmt = useFormat()
  const g = useGate()
  const [sheet, setSheet] = useState<null | 'type' | 'spoil' | 'close'>(null)
  const book = g.roster?.receiptBooks.find((b) => b.id === session.paper?.book_id) ?? null
  const typed = useMemo(
    () =>
      g.outbox
        .filter((r) => r.type === 'commit_delivery' && r.session_id === session.id)
        .map(
          (r) =>
            (r.payload as CommitDeliveryPayload).receipt_slip?.slip_no ??
            (r.payload as CommitDeliveryPayload).slip.slip_no,
        ),
    [g.outbox, session.id],
  )
  const spoiled = useMemo(() => spoiledSlips(g.outbox, session.id), [g.outbox, session.id])
  const usedHere = [...typed, ...spoiled]
  const missing = missingSlips(usedHere)
  const sorted = [...usedHere].sort((a, b) => Number(a) - Number(b))
  const taken = new Set([...usedHere.map(Number), ...(book?.used_slip_nos ?? [])])

  if (sheet === 'close') {
    return (
      <GateScreen title="gate.paper.close" back={() => setSheet(null)}>
        {missing.length > 0 && (
          <Banner tone="error">{t('gate.paper.missing', { slips: missing.join(', ') })}</Banner>
        )}
        <CloseDayForm
          session={session}
          blockers={
            missing.length > 0 ? [t('gate.paper.missing', { slips: missing.join(', ') })] : []
          }
          slipRange={
            book && sorted.length > 0
              ? { book_id: book.id, first_slip_no: sorted[0]!, last_slip_no: sorted.at(-1)! }
              : null
          }
        />
      </GateScreen>
    )
  }

  return (
    <GateScreen title="gate.paper.title">
      <Card>
        <CardHeader
          eyebrow={t('gate.paper.short')}
          title={<span className="font-mono text-[16px]">{session.code}</span>}
        />
        <CardBody className="flex flex-col gap-1 text-[14px]">
          <div>
            {t('gate.paper.operator')}: <b>{session.operator_name}</b>
          </div>
          <div>
            {t('gate.paper.book')}: <b>{session.paper?.book_no}</b> · {fmt.date(session.opened_at)}
          </div>
          <div className="font-mono text-[13px]">
            {t('gate.paper.typedSlips', { n: typed.length })} ·{' '}
            {t('gate.paper.spoiledSlips', { n: spoiled.length })}
          </div>
          {sorted.length > 0 && (
            <div className="font-mono text-[12px] text-muted">{sorted.join(', ')}</div>
          )}
          {missing.length > 0 && (
            <Banner tone="warn">{t('gate.paper.missing', { slips: missing.join(', ') })}</Banner>
          )}
        </CardBody>
      </Card>
      <Button
        size="xl"
        fullWidth
        leadingIcon={<PencilSimpleLine aria-hidden="true" />}
        onClick={() => setSheet('type')}
      >
        {t('gate.paper.typeSlip')}
      </Button>
      <Button
        size="touch"
        variant="secondary"
        fullWidth
        leadingIcon={<Prohibit aria-hidden="true" />}
        onClick={() => setSheet('spoil')}
      >
        <BiText k="gate.paper.spoiled" />
      </Button>
      <Button
        size="touch"
        variant="secondary"
        fullWidth
        leadingIcon={<Notebook aria-hidden="true" />}
        onClick={() => setSheet('close')}
      >
        <BiText k="gate.paper.close" />
      </Button>
      {(sheet === 'type' || sheet === 'spoil') && book && (
        <SlipSheet
          mode={sheet}
          session={session}
          range={[book.first_slip_no, book.last_slip_no]}
          taken={taken}
          onClose={() => setSheet(null)}
          onType={(h) => {
            setSheet(null)
            onType({ ...h, book_id: book.id, book_no: book.book_no })
          }}
        />
      )}
      {(sheet === 'type' || sheet === 'spoil') && !book && (
        <Banner tone="error">{t('gate.paper.noBook')}</Banner>
      )}
    </GateScreen>
  )
}

function SlipSheet({
  mode,
  session,
  range,
  taken,
  onClose,
  onType,
}: {
  mode: 'type' | 'spoil'
  session: GateSession
  range: [number, number]
  taken: Set<number>
  onClose: () => void
  onType: (h: Omit<PaperSlipHeader, 'book_id' | 'book_no'>) => void
}) {
  const { t } = useI18n()
  const toast = useToast()
  const actor = useActor()
  const [slip, setSlip] = useState('')
  const [time, setTime] = useState('')
  const [photo, setPhoto] = useState<string | null>(null)
  const n = Number(slip)
  const slipErr =
    slip === ''
      ? null
      : n < range[0] || n > range[1]
        ? t('gate.paper.notInBook', { from: range[0], to: range[1] })
        : taken.has(n)
          ? t('gate.paper.slipUsed')
          : null
  const dateKey = istDateKey(session.paper?.day_opened_at ?? session.opened_at)
  const slipTime = istInstant(dateKey, time)
  const ok = slip !== '' && !slipErr && !!photo && (mode === 'spoil' || !!slipTime)
  return (
    <Sheet
      open
      onClose={onClose}
      title={t(mode === 'type' ? 'gate.paper.typeSlip' : 'gate.paper.spoiled')}
      footer={
        <Button
          size="touch"
          fullWidth
          variant={mode === 'spoil' ? 'danger' : 'primary'}
          disabled={!ok}
          onClick={async () => {
            if (!ok || !photo) return
            if (mode === 'type' && slipTime)
              onType({ slip_no: slip, slip_time: slipTime, page_photo_media_id: photo })
            else if (session.paper?.book_id) {
              await spoilSlip(session, actor.user_id, {
                book_id: session.paper.book_id,
                slip_no: slip,
                photo_id: photo,
              })
              toast.success(t('gate.paper.spoiledSaved', { slip }))
              onClose()
            }
          }}
        >
          {t(mode === 'type' ? 'common.next' : 'common.save')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t('gate.slip.slipNo')} required error={slipErr ?? undefined}>
          <Input
            size="touch"
            inputMode="numeric"
            className="font-mono"
            value={slip}
            onChange={(e) => setSlip(e.target.value.replace(/\D/g, '').slice(0, 8))}
          />
        </Field>
        {mode === 'type' && (
          <Field label={t('gate.paper.slipTime')} required>
            <Input
              size="touch"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </Field>
        )}
        <Field label={t('gate.paper.pagePhoto')} required>
          <PhotoCapture kind="document" value={photo} onChange={setPhoto} />
        </Field>
      </div>
    </Sheet>
  )
}
